import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Users, User, MapPin, Route, AlertCircle } from "lucide-react";
import ConsumerHistoryCard from "./ConsumerHistoryCard";
import { TabProps } from "@features/mx/device-management/types";
import {
  useDeviceAllocationHistory,
  useDeviceDetail,
} from "@features/mx/device-management/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";

const AllocationTab: React.FC<TabProps> = ({ tab, meterId }) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: details } = useDeviceDetail(meterId, remoteUtilityId);
  const { data: allocationDetail } = useDeviceAllocationHistory({
    meterNumber: details?.meterNumber,
    deviceNo: details?.deviceNo,
  });
  const navigate = useNavigate();

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-3">
        <div className="flex justify-between items-center">
       <PageHeader
  title="Consumer & Premise Allocation"
  icon={Users}
/>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        {allocationDetail?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-5 space-y-3">
              <h3 className="text-sm font-medium flex items-center">
                <User className="h-3.5 w-3.5 mr-1.5 text-primary" />
                Consumer History
              </h3>
              <div className="space-y-3">
                {allocationDetail?.map((consumer) => (
                  <ConsumerHistoryCard
                    key={consumer?.consumerInfo?.id}
                    consumer={consumer}
                  />
                ))}
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <h3 className="text-sm font-medium mb-2 flex items-center">
                    <MapPin className="h-3.5 w-3.5 mr-1.5 text-emerald-500" />
                    Premise Details
                  </h3>
                  <Card className="border shadow-sm h-[calc(100%-28px)]">
                    <CardContent className="p-3">
                      <div className="grid grid-cols-1 gap-y-2 gap-x-4 text-xs">
                        <div>
                          <p className="text-muted-foreground">Premise</p>
                          <p className="font-medium">
                            {details?.premiseName ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Area</p>
                          <p className="font-medium">
                            {details?.areaName ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Sub Area</p>
                          <p className="font-medium">
                            {details?.subareaName ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Utility Service
                          </p>
                          <p className="font-medium">
                            {details?.utilityService ?? "NA"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="text-sm font-medium mb-2 flex items-center">
                    <Route className="h-3.5 w-3.5 mr-1.5 text-blue-500" />
                    Route & Reading Cycle
                  </h3>
                  <Card className="border shadow-sm h-[calc(100%-28px)]">
                    <CardContent className="p-3">
                      <div className="grid grid-cols-1 gap-y-2 gap-x-4 text-xs">
                        <div>
                          <p className="text-muted-foreground">Route</p>
                          <p className="font-medium">
                            {details?.route ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Read Cycle</p>
                          <p className="font-medium">
                            {details?.meterCycle ?? "NA"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Total Consumption
                          </p>
                          <p className="font-medium">
                            {details?.totalConsumptionOfConsumer ?? "NA"}{" "}
                            {details?.utilityServiceUnit ?? "NA"}
                          </p>
                        </div>
                        <div></div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center">
            <div className="mx-auto w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mb-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="text-sm font-medium mb-1">Not Assigned</h3>
            <p className="text-xs text-muted-foreground mb-4">
              This meter is not assigned to any consumer or premise.
            </p>
            <Button
              size="sm"
              onClick={() =>
                navigate(`/mx/device-management/meter/edit/${meterId}`)
              }
            >
              <User className="h-4 w-4 mr-1" />
              Assign Consumer
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AllocationTab;
